/**
 * Wingman Spatial Validation Engine (Pure Deterministic Mode)
 * 
 * 🔒 CORE LAW (NON-NEGOTIABLE)
 * ALL coordinates MUST follow the Wingman Coordinate Contract.
 * 
 * This module is a PURE logic layer. It does not own authority or state.
 */

// ============================================================================
// SHARED SPATIAL CONTRACT (Merged for build stability)
// ============================================================================

export interface LngLatObj {
    lng: number;
    lat: number;
}

export type LngLatArray = [number, number];

/**
 * Validates if a coordinate is within legal Earth bounds.
 */
export function isValidCoordinate(input: any): boolean {
    if (!input) return false;

    let lng, lat;

    if (Array.isArray(input)) {
        if (input.length !== 2) return false;
        [lng, lat] = input;
    } else if (typeof input === 'object') {
        lng = input.lng ?? input.lon ?? input.longitude;
        lat = input.lat ?? input.latitude;
    }

    return (
        typeof lng === 'number' && Number.isFinite(lng) &&
        typeof lat === 'number' && Number.isFinite(lat) &&
        lng >= -180 && lng <= 180 &&
        lat >= -90 && lat <= 90
    );
}

/**
 * Heuristic to detect potential [lat, lng] swaps.
 * If the second value (intended lat) is outside [-90, 90], it's a swap.
 */
export function detectSwap(coords: LngLatArray): LngLatArray {
    const [lng, lat] = coords;
    // If lat is outside [-90, 90] but lng is within it, it's almost certainly swapped.
    if ((lat < -90 || lat > 90) && (lng >= -90 && lng <= 90)) {
        return [lat, lng];
    }
    return coords;
}

/**
 * Normalizes any coordinate-like input into the authoritative [lng, lat] array.
 * 🛡️ ATOM W-007: Enforces strict contract and detects common provider swaps.
 */
export function normalizeCoordinate(input: any, source: string = 'unknown'): LngLatArray | null {
    if (!input) return null;

    let lng: number | null = null;
    let lat: number | null = null;

    try {
        // Array Handling
        if (Array.isArray(input) && input.length >= 2) {
            lng = Number(input[0]);
            lat = Number(input[1]);
        } 
        // Object Handling
        else if (typeof input === 'object') {
            lng = Number(input.lng ?? input.lon ?? input.longitude ?? (Array.isArray(input.center) ? input.center[0] : input.center?.lng ?? input.center?.lon));
            lat = Number(input.lat ?? input.latitude ?? (Array.isArray(input.center) ? input.center[1] : input.center?.lat));
        }

        if (lng === null || lat === null || !Number.isFinite(lng) || !Number.isFinite(lat)) {
            return null;
        }

        let coerced = [lng, lat] as LngLatArray;
        
        // 🛡️ ATOM W-007: Automated Swap Detection
        // Only apply if the input was an array (ambiguous), not an explicit lat/lng object
        if (Array.isArray(input) || (typeof input === 'object' && Array.isArray(input.center))) {
            coerced = detectSwap(coerced);
        }

        // Final Bounds Check
        if (coerced[0] < -180 || coerced[0] > 180 || coerced[1] < -90 || coerced[1] > 90) {
            return null;
        }

        return coerced;
    } catch (e) {
        return null;
    }
}

/**
 * Standard Haversine distance calculation (meters).
 */
export function calculateDistanceHaversine(a: any, b: any): number {
    const normA = normalizeCoordinate(a);
    const normB = normalizeCoordinate(b);

    if (!normA || !normB) return Infinity;

    const [lng1, lat1] = normA;
    const [lng2, lat2] = normB;

    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lng2 - lng1) * Math.PI / 180;
    
    const x = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
    return R * c;
}

// ============================================================================
// VALIDATION LOGIC
// ============================================================================

export interface CoordinateValidationResult {
  valid: boolean;
  coordinates?: LngLatArray;
  error?: string;
  reason?: 'invalid' | 'duplicate' | 'jitter' | 'valid';
}

/**
 * Validates spatial intent and enforces jitter/duplicate suppression.
 */
export function validateSpatialIntent(
    input: any, 
    currentCenter?: LngLatArray | null,
    source: string = 'AI'
): CoordinateValidationResult {
    // Check if it's a bounds-only command
    if (input && input.bounds && !input.center && !input.lat && !input.lng && !input.lon) {
        if (isValidBounds(input.bounds)) {
            return { valid: true, reason: 'valid' };
        }
        return { valid: false, error: 'Invalid or out-of-bounds coordinates', reason: 'invalid' };
    }

    const normalized = normalizeCoordinate(input, 'validateSpatialIntent');
    if (!normalized || !isValidCoordinate(normalized)) {
        return { 
            valid: false, 
            error: 'Invalid or out-of-bounds coordinates',
            reason: 'invalid'
        };
    }

    if (currentCenter) {
        const distance = calculateDistanceHaversine(currentCenter, normalized);
        
        // 🔒 ATOM C2.C: Dynamic Jitter Governance
        // USER needs high precision (1m), AI/SYNC needs high stability (10m)
        const jitterThreshold = source === 'USER' ? 1.0 : 10.0;
        const duplicateThreshold = 0.1;

        if (distance < duplicateThreshold) {
            return {
                valid: false,
                coordinates: normalized,
                reason: 'duplicate',
                error: `Duplicate detected: ${distance.toFixed(4)}m < ${duplicateThreshold}m`
            };
        }

        if (distance < jitterThreshold) {
            return {
                valid: false,
                coordinates: normalized,
                reason: 'jitter',
                error: `Jitter suppressed: ${distance.toFixed(2)}m < ${jitterThreshold}m (Source: ${source})`
            };
        }
    }

    return { 
        valid: true, 
        coordinates: normalized,
        reason: 'valid' 
    };
}

/**
 * LEGACY WRAPPER (for phased migration)
 */
export function validateCoordinates(input: any): 'ALLOW' | 'BLOCK' | 'RETRY' | 'FAIL_SAFE' {
    if (input && input.authorityState === 'USER_DRAGGING') return 'RETRY';
    if (input && input.authorityState === 'SYSTEM_LOCK') return 'FAIL_SAFE';

    const result = validateSpatialIntent(input, input.currentCenter);
    if (result.valid) return 'ALLOW';
    return 'BLOCK';
}

/**
 * Bounds Validation
 */
export function isValidBounds(bounds: any): boolean {
    if (!Array.isArray(bounds) || bounds.length !== 2) return false;
    const [sw, ne] = bounds;
    return isValidCoordinate(sw) && isValidCoordinate(ne);
}

/**
 * Coerces geographic keywords/regions into precise coordinates.
 */
export function coerceCoordinates(input: any, regionHint?: string): LngLatArray | null {
    const normalized = normalizeCoordinate(input);
    if (normalized) return normalized;

    // Region-based defaults for missing/partial coords
    if (regionHint === 'americas') return [-98.5, 39.8];
    if (regionHint === 'europe') return [10.4, 51.1];
    if (regionHint === 'asia') return [100.6, 34.0];
    
    return null;
}

// Expose to window for tests
if (typeof window !== 'undefined') {
    (window as any).WingmanSpatialUtils = {
        isValidCoordinate,
        normalizeCoordinate,
        calculateDistanceHaversine,
        validateSpatialIntent,
        coerceCoordinates
    };
}
